// Lo que dice Cumbre en las primeras piezas (ADR-2026-10-02-onboarding-v2-piezas-con-cumbre-y-tienda).
// En neutro, como todo el onboarding (ADR-2026-10-02-onboarding-v2-todo-en-neutro).
//
// Las opciones de qué crear son las MISMAS del cuestionario ("¿Qué te gustaría crear
// primero?"); qué genera el motor con cada una lo sigue decidiendo `objetivoSugerido`.

import type { Reaccion, Tramo } from './dialogo';
import { nombreDelObjetivo, objetivoSugerido } from './piezas';
import { PREGUNTAS, type Pregunta } from './preguntas';

/** Las opciones de la pregunta "primero" del cuestionario (sin "Otro", que va aparte). */
export const OPCIONES_DE_LO_QUE_SE_CREA: Pregunta['opciones'] = PREGUNTAS.find((q) => q.clave === 'primero')?.opciones ?? [];

const enMinuscula = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/**
 * Lo que se va a crear, como va dentro de una frase. Para "Videos" es lo que el motor arma de
 * verdad (las primeras piezas son imágenes); lo que escribió en "Otro", tal cual.
 */
export function queSeCrea(primero: string, otro: string): string {
  if (primero === 'otro') return otro.trim() || 'lo que pediste';
  if (primero === 'videos') return enMinuscula(nombreDelObjetivo(objetivoSugerido('videos')));
  const opcion = OPCIONES_DE_LO_QUE_SE_CREA.find((o) => o.id === primero);
  return enMinuscula(opcion?.label ?? nombreDelObjetivo(objetivoSugerido(primero)));
}

/**
 * El globo del paso. `conTienda`: la tienda está conectada y se puede elegir de su catálogo.
 * `pidioVideo`: respondió "Videos" — las primeras piezas son imágenes, y se lo dice.
 */
export function dialogoDePiezas(p: { queCrea: string; conTienda: boolean; pidioVideo: boolean }): Tramo[] {
  const como = p.conTienda ? 'Elige uno de tus productos o sube una foto' : 'Sube una foto de tu producto';
  const detalle = p.pidioVideo
    ? `Los videos los creas después, desde Atlas. Para empezar: ${enMinuscula(como)}, y armo 3 opciones de ${p.queCrea} con tu marca.`
    : `${como} y armo 3 opciones de ${p.queCrea} con tu marca.`;
  return [
    { texto: '¡Último paso!', estilo: 'saludo' },
    { texto: 'Creemos tus primeras piezas', estilo: 'pregunta' },
    { texto: detalle, estilo: 'detalle' },
  ];
}

/** Al elegir un producto (de la tienda o la foto subida). */
export function reaccionAlProducto(nombre: string, n: number): Reaccion {
  const cual = nombre.trim();
  return { id: `producto-${n}`, texto: cual ? `Perfecto, uso ${cual}.` : 'Perfecto, uso esa foto.' };
}

/** Al cambiar qué se va a crear. */
export function reaccionAlObjetivo(primero: string, otro: string, n: number): Reaccion {
  const texto =
    primero === 'videos'
      ? `Los videos los creas después, desde Atlas. Por ahora armo ${queSeCrea(primero, otro)}.`
      : `Hecho: armo ${queSeCrea(primero, otro)}.`;
  return { id: `objetivo-${n}`, texto };
}

export const REACCION_A_LA_FOTO_INVALIDA: Reaccion = {
  id: 'foto-invalida',
  texto: 'Esa foto no la pude leer. Prueba con otra, en JPG o PNG.',
};

// ─── El revelado ──────────────────────────────────────────────────────────────

/**
 * Cumbre presenta cada pieza: cuál es (de cuántas), cómo se llama y por qué. Las que fallan no
 * se cobran (1 crédito por imagen ENTREGADA), y se lo dice.
 */
export function dialogoDelRevelado(p: {
  n: number;
  total: number;
  etiqueta: string;
  porques: readonly string[];
  estado: 'lista' | 'creando' | 'fallo';
}): Tramo[] {
  const cual = `${p.n + 1} de ${p.total}`;
  const saludo =
    p.estado === 'lista' && p.n === 0 ? '¡Tus piezas están listas!' : p.n === p.total - 1 && p.total > 1 ? `La última: ${cual}` : cual;
  const detalle =
    p.estado === 'fallo'
      ? 'Esta no salió, y no se cobró. Sigue con las otras.'
      : p.estado === 'creando'
        ? 'La estoy terminando: aparece aquí en un momento.'
        : p.porques.join(' ') || 'Hecha con tu marca.';
  return [
    { texto: saludo, estilo: 'saludo' },
    { texto: p.etiqueta, estilo: 'pregunta' },
    { texto: detalle, estilo: 'detalle' },
  ];
}
